import {
  TrackingEvent,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import Image, { ImageLoader } from 'next/image'
import Link from 'next/link'
import React from 'react'

import { replaceNewLinesWithBreaks } from '../utils/create-breaks'
import styles from './hero-card.module.css'

export interface HeroCardProps {
  heading: string
  subheading: string
  image: {
    filename: string
    alt?: string
  }
  cta?: {
    label: string
    url: string
  }[]
  imageLoader?: ImageLoader
  linkClickTrackingEvent?: TrackingEvent
}

export const HeroCard = ({
  heading,
  subheading,
  image,
  cta,
  imageLoader,
  linkClickTrackingEvent,
}: HeroCardProps) => {
  const { trackEvent } = useAnalytics()

  const onHeroLinkClick = (event: React.MouseEvent) => {
    // Prevent the click event from bubbling up to parent elements (e.g., carousel slide)
    // to avoid triggering unintended slide tracking events when clicking the CTA link
    event.stopPropagation()

    const defaultTrackingEvent = {
      eventName: `promo hero link`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: 'click',
          category: 'link',
          label: `Promo link click`,
          action: `Promo Link link click`,
        },
      },
    }
    trackEvent(linkClickTrackingEvent ?? defaultTrackingEvent)
  }

  return (
    <div className={styles.container}>
      <div className={styles.topContent}>
        <h2 className={styles.heading}>{replaceNewLinesWithBreaks(heading)}</h2>
        <h3 className={styles.subheading}>
          {replaceNewLinesWithBreaks(subheading)}
        </h3>
        {cta?.[0] && (
          <Link
            className={styles.link}
            href={cta[0].url}
            onClick={onHeroLinkClick}
          >
            {cta[0].label}
          </Link>
        )}
      </div>
      <div className={styles.imageContainer}>
        <Image
          src={image.filename}
          alt={image.alt ?? 'home hero offer representative image'}
          fill={true}
          style={{
            objectFit: 'contain',
          }}
          priority={true}
          loading="eager"
          loader={imageLoader}
        />
      </div>
    </div>
  )
}
